import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const TermsOfServicePage: React.FC = () => {
  const { t } = useLanguage();

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-2 h-2 bg-system-blue rounded-full animate-pulse shadow-neon-blue"></div>
          <span className="font-orbitron text-2xl tracking-wider text-system-blue uppercase">
            ［ Terms of Service ］
          </span>
        </div>
        <p className="text-sm text-gray-400 font-rajdhani">
          Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
      </div>

      {/* Content */}
      <div className="system-window rounded-lg p-6 max-w-4xl">
        <div className="prose prose-invert max-w-none">
          <div className="space-y-6 text-gray-300 font-rajdhani">
            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-blue mb-3">1. Acceptance of Terms</h2>
              <p className="leading-relaxed mb-3">
                By accessing or using AwakenArc ("the Service," "Platform," "we," "us," or "our"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Service.
              </p>
              <p className="leading-relaxed">
                These Terms constitute a legally binding agreement between you and AwakenArc. By creating an account, you represent that you are at least 13 years of age and have the legal capacity to enter into this agreement.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-blue mb-3">2. Description of Service</h2>
              <p className="leading-relaxed mb-3">
                AwakenArc is a gamified productivity platform that transforms daily tasks and goals into an RPG-style quest system. The Service provides:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                <li>Task management through a quest system</li>
                <li>AI-powered quest and program generation</li>
                <li>Progress tracking with levels, stats, and achievements</li>
                <li>Subscription-based premium features</li>
                <li>Data export and account management tools</li>
              </ul>
              <p className="leading-relaxed">
                The Service is provided on an "as is" and "as available" basis. We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time, with or without notice.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-blue mb-3">3. Age Requirement and Eligibility</h2>
              <p className="leading-relaxed mb-3">
                You must be at least 13 years old to use AwakenArc. By using the Service, you represent and warrant that:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                <li>You are at least 13 years of age</li>
                <li>You have the legal capacity to enter into these Terms</li>
                <li>You will provide accurate and complete information during registration</li>
                <li>You will maintain the security of your account credentials</li>
                <li>You will comply with all applicable laws and regulations</li>
              </ul>
              <p className="leading-relaxed">
                If you are under 18, you must have permission from a parent or legal guardian to use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-blue mb-3">4. User Accounts</h2>
              <p className="leading-relaxed mb-3">
                <strong>Account Creation:</strong> To access certain features, you must create an account using a valid email address or through Google authentication. You must verify your email address to fully activate your account.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>Account Security:</strong> You are solely responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                <li>Use a strong, unique password</li>
                <li>Not share your account with others</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>Be responsible for all activities under your account</li>
              </ul>
              <p className="leading-relaxed">
                <strong>Account Termination:</strong> You may delete your account at any time through the Settings page. We reserve the right to suspend or terminate accounts that violate these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-blue mb-3">5. Subscription Plans and Pricing</h2>
              <p className="leading-relaxed mb-3">
                <strong>Free Trial:</strong> New users receive a 14-day free trial with full access to S-Rank features. No credit card is required for the trial. After the trial expires, your account will be locked until you subscribe to a paid plan.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>Subscription Tiers:</strong> We offer the following subscription plans:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                <li><strong>E-Rank Hunter:</strong> $2.99/month (50% promotional discount, regularly $5.99)</li>
                <li><strong>S-Rank Hunter:</strong> $4.99/month (50% promotional discount, regularly $9.99)</li>
              </ul>
              <p className="leading-relaxed mb-3">
                <strong>Billing and Renewal:</strong> Subscriptions automatically renew on a monthly basis unless cancelled. You will be charged at the beginning of each billing cycle. We reserve the right to change subscription prices with 30 days' notice.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>Upgrades and Downgrades:</strong> You may upgrade or downgrade your subscription at any time:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                <li><strong>Upgrades:</strong> Take effect immediately with prorated charges</li>
                <li><strong>Downgrades:</strong> Take effect at the start of your next billing cycle</li>
              </ul>
              <p className="leading-relaxed">
                <strong>Cancellation:</strong> You may cancel your subscription at any time. Upon cancellation, you will retain access until the end of your current billing period. Refunds are available within 14 days for new subscriptions or as outlined in our Refund Policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-blue mb-3">6. Payment Terms</h2>
              <p className="leading-relaxed mb-3">
                Payments are processed through Paddle, our third-party payment processor. By providing payment information, you:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                <li>Authorize us to charge your payment method for all applicable fees</li>
                <li>Represent that you are authorized to use the payment method</li>
                <li>Agree to provide accurate and complete payment information</li>
                <li>Agree to update payment information if it changes</li>
              </ul>
              <p className="leading-relaxed">
                If payment fails, we may suspend your account until payment is received. You are responsible for any fees charged by your payment provider.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-blue mb-3">7. User Content and Ownership</h2>
              <p className="leading-relaxed mb-3">
                <strong>Your Content:</strong> You retain all ownership rights to the content you create on AwakenArc, including quests, tasks, notes, and personal data ("User Content").
              </p>
              <p className="leading-relaxed mb-3">
                <strong>License to Us:</strong> By creating User Content, you grant us a limited, non-exclusive, royalty-free license to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                <li>Store and process your content to provide the Service</li>
                <li>Create backups of your content</li>
                <li>Use anonymized data for service improvements</li>
                <li>Display testimonials with your permission</li>
              </ul>
              <p className="leading-relaxed mb-3">
                <strong>Content Sharing:</strong> Your quests and tasks are private by default and cannot be shared with other users. We will not share your User Content with third parties except as necessary to provide the Service or as required by law.
              </p>
              <p className="leading-relaxed">
                <strong>Data Export:</strong> You may export your User Content at any time through your account settings.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-blue mb-3">8. AI-Generated Content</h2>
              <p className="leading-relaxed mb-3">
                AwakenArc uses OpenAI's technology to generate personalized quests and programs. By using AI features:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                <li>Your prompts and inputs are sent to OpenAI for processing</li>
                <li>AI-generated content is stored in your account</li>
                <li>You can delete AI-generated content at any time</li>
                <li>We are working to implement anonymization of AI prompts</li>
              </ul>
              <p className="leading-relaxed">
                You acknowledge that AI-generated content may not always be accurate, appropriate, or suitable for your needs. We are not responsible for the content, accuracy, or quality of AI-generated quests or programs.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-blue mb-3">9. Acceptable Use Policy</h2>
              <p className="leading-relaxed mb-3">
                You agree NOT to use the Service to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe on intellectual property rights of others</li>
                <li>Transmit harmful code, viruses, or malware</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Engage in fraudulent, deceptive, or illegal activities</li>
                <li>Use automated systems (bots, scrapers) without permission</li>
                <li>Resell or redistribute the Service</li>
                <li>Reverse engineer or attempt to extract source code</li>
              </ul>
              <p className="leading-relaxed">
                Violation of this Acceptable Use Policy may result in immediate termination of your account without refund.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-blue mb-3">10. Intellectual Property Rights</h2>
              <p className="leading-relaxed mb-3">
                <strong>Platform Ownership:</strong> All rights, title, and interest in AwakenArc, including all code, design, features, functionality, graphics, and content (excluding User Content), are owned by AwakenArc and are protected by international copyright, trademark, and other intellectual property laws.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>Trademarks:</strong> "AwakenArc" and related logos are trademarks of AwakenArc. You may not use our trademarks without our prior written permission.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>Third-Party Assets:</strong> The Service may include assets generated by AI or sourced from the internet. While we make reasonable efforts to use properly licensed materials, if you believe your copyright has been infringed, please contact us at umarkhodzha@awakenarc.com.
              </p>
              <p className="leading-relaxed">
                <strong>License to You:</strong> We grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for personal, non-commercial purposes in accordance with these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-blue mb-3">11. Service Availability and Data Loss</h2>
              <p className="leading-relaxed mb-3">
                <strong>Uptime:</strong> While we strive to provide reliable service, we do not guarantee uninterrupted or error-free operation. The Service may be unavailable due to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                <li>Scheduled maintenance (we will notify users in advance)</li>
                <li>Emergency repairs or updates</li>
                <li>Events beyond our control (server failures, internet outages, etc.)</li>
              </ul>
              <p className="leading-relaxed mb-3">
                <strong>Data Backups:</strong> We perform automatic backups every 14 days. However, you are responsible for maintaining your own backups of important data using our export feature.
              </p>
              <p className="leading-relaxed">
                <strong>Data Loss Liability:</strong> While we take reasonable precautions to protect your data, we are not liable for any data loss, corruption, or service interruption. We strongly recommend regularly exporting your data.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-blue mb-3">12. Disclaimers and Limitation of Liability</h2>
              <p className="leading-relaxed mb-3">
                <strong>Service Disclaimer:</strong> THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>No Guarantee of Results:</strong> AwakenArc is a productivity tool. We do not guarantee that using the Service will result in specific outcomes, achievements, or personal improvements. Your results depend on your own effort and commitment.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>Limitation of Liability:</strong> TO THE MAXIMUM EXTENT PERMITTED BY LAW, AWAKENARC SHALL NOT BE LIABLE FOR:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                <li>Indirect, incidental, special, consequential, or punitive damages</li>
                <li>Loss of profits, revenue, data, or business opportunities</li>
                <li>Service interruptions or data loss</li>
                <li>Errors in AI-generated content</li>
                <li>Unauthorized access to your account due to your own negligence</li>
              </ul>
              <p className="leading-relaxed">
                IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM, OR $100, WHICHEVER IS GREATER.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-blue mb-3">13. Indemnification</h2>
              <p className="leading-relaxed">
                You agree to indemnify, defend, and hold harmless AwakenArc and its officers, directors, employees, and agents from any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising from: (a) your use of the Service; (b) your violation of these Terms; (c) your violation of any rights of another party; or (d) your User Content.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-blue mb-3">14. Termination</h2>
              <p className="leading-relaxed mb-3">
                <strong>Termination by You:</strong> You may terminate your account at any time by deleting it through your account settings. Upon deletion, your data will be permanently removed from our systems (excluding backups, which are deleted after their retention period).
              </p>
              <p className="leading-relaxed mb-3">
                <strong>Termination by Us:</strong> We may suspend or terminate your account immediately, without prior notice or liability, for any reason, including:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                <li>Violation of these Terms</li>
                <li>Fraudulent or illegal activity</li>
                <li>Abuse of the Service or other users</li>
                <li>Non-payment of subscription fees</li>
                <li>At our sole discretion</li>
              </ul>
              <p className="leading-relaxed">
                <strong>Effect of Termination:</strong> Upon termination, your right to use the Service will immediately cease. You will not be entitled to any refund of prepaid fees except as required by law.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-blue mb-3">15. Dispute Resolution</h2>
              <p className="leading-relaxed mb-3">
                <strong>Informal Resolution:</strong> If you have a dispute with AwakenArc, please contact us at umarkhodzha@awakenarc.com. We will make reasonable efforts to resolve the issue through good-faith negotiation.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>Mediation:</strong> If we cannot resolve the dispute informally, both parties agree to attempt mediation before pursuing legal action.
              </p>
              <p className="leading-relaxed">
                <strong>Governing Law:</strong> These Terms shall be governed by and construed in accordance with applicable international laws and the laws of the jurisdictions where our users are located, without regard to conflict of law principles.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-blue mb-3">16. International Users</h2>
              <p className="leading-relaxed mb-3">
                AwakenArc is accessible worldwide. By using the Service from outside the United States, you acknowledge that:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                <li>Your data may be transferred to and processed in the United States</li>
                <li>You consent to such transfer and processing</li>
                <li>You are responsible for compliance with local laws</li>
                <li>We do not block access from any region</li>
              </ul>
              <p className="leading-relaxed">
                <strong>EU Users:</strong> If you are located in the European Union, you have additional rights under the GDPR. Please see our Privacy Policy for details.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-blue mb-3">17. Changes to Terms</h2>
              <p className="leading-relaxed mb-3">
                We reserve the right to modify these Terms at any time. When we make changes:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                <li>We will update the "Last Updated" date at the top of this page</li>
                <li>We will notify you via email and in-app notification</li>
                <li>Changes will take effect automatically 30 days after notification</li>
                <li>Continued use of the Service constitutes acceptance of the new Terms</li>
              </ul>
              <p className="leading-relaxed">
                Material changes that affect your rights will be prominently announced. If you do not agree to the modified Terms, you must stop using the Service and may delete your account.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-blue mb-3">18. Miscellaneous</h2>
              <p className="leading-relaxed mb-3">
                <strong>Entire Agreement:</strong> These Terms, together with our Privacy Policy and Refund Policy, constitute the entire agreement between you and AwakenArc.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>Severability:</strong> If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>Waiver:</strong> Our failure to enforce any right or provision of these Terms will not be deemed a waiver of such right or provision.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>Assignment:</strong> You may not assign or transfer these Terms without our prior written consent. We may assign these Terms without restriction.
              </p>
              <p className="leading-relaxed">
                <strong>Language:</strong> These Terms are provided in English and may be translated into other languages. In case of conflict, the English version prevails.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-blue mb-3">19. Contact Information</h2>
              <p className="leading-relaxed mb-3">
                If you have any questions, concerns, or feedback about these Terms of Service, please contact us:
              </p>
              <div className="bg-system-darker border border-system-blue/30 rounded-lg p-4">
                <p className="leading-relaxed mb-2">
                  <strong className="text-system-blue">Email:</strong> umarkhodzha@awakenarc.com
                </p>
                <p className="leading-relaxed mb-2">
                  <strong className="text-system-blue">Support:</strong> <a href="https://www.awakenarc.com/support" target="_blank" rel="noopener noreferrer" className="text-system-blue hover:underline">www.awakenarc.com/support</a>
                </p>
                <p className="leading-relaxed">
                  <strong className="text-system-blue">Response Time:</strong> We aim to respond to all inquiries within 48 hours
                </p>
              </div>
            </section>

            <section className="mt-8 pt-8 border-t border-gray-700">
              <p className="text-sm text-gray-500 leading-relaxed">
                By using AwakenArc, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. Thank you for being part of our community of hunters leveling up their lives! ⚔️
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;

